import { FormEvent, useCallback, useState } from 'react'

import {
  Button,
  Center,
  Group,
  Input,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { Link } from '@mui/material'
import { IconArrowBackUp, IconCircleCheckFilled } from '@tabler/icons-react'
import { useNotify, useTranslate } from 'react-admin'
import { usePostApiUsersResetPasswordConfirmation } from '../../codegen/generated/users'

type NewPasswordProps = {
  email: string
  token: string
}

export const NewPassword = ({ email, token }: NewPasswordProps) => {
  const notify = useNotify()
  const t = useTranslate()

  const [password, setPassword] = useState('')

  const newPasswordMutation = usePostApiUsersResetPasswordConfirmation()

  const onNewPassword = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      try {
        await newPasswordMutation.mutateAsync({
          data: {
            email,
            password,
            token,
          },
        })
      } catch (error: any) {
        console.log(JSON.stringify(error))
        console.error(error)

        if (error.response.status === 404) {
          notifications.show({
            color: 'red',
            title: `${t('_generics.error')} - 404`,
            message: t('pages.login.errors.userNotFound'),
          })
        } else {
          notifications.show({
            color: 'red',
            title: `${t('_generics.error')} - ${error.response?.status}`,
            message: error.response?.message ?? t('_generics.unknownError'),
          })
        }
      }
    },
    [t, newPasswordMutation.mutateAsync, email, password, notify, token],
  )

  return (
    <form onSubmit={onNewPassword}>
      <Center>
        <Title size="h4" ta="center">
          {t('pages.login.newPassword.title')}
        </Title>
      </Center>
      <Stack w={300} p="md" pt="xs" pb="md" gap="xs">
        {newPasswordMutation.isSuccess ? (
          <Stack>
            <Group justify="center" align="center" wrap="nowrap" mt="md">
              <IconCircleCheckFilled color="var(--mantine-color-green-6)" size={28} />
              <Text ta="center" c="green">
                {t('pages.login.newPassword.description')}
              </Text>
            </Group>
            <Link href="/login" fontSize={12}>
              <Group align="center" justify="center" wrap="nowrap" gap="xs" mt="xs">
                <IconArrowBackUp size={18} />
                <Text size="sm">{t('pages.login.backToLogin')}</Text>
              </Group>
            </Link>
          </Stack>
        ) : (
          <>
            <Input.Wrapper label={t('_generics.email')} id="email">
              <TextInput id="email" name="email" type="email" size="md" value={email} disabled />
            </Input.Wrapper>
            <Input.Wrapper label={t('_generics.password')} id="password">
              <PasswordInput
                id="password"
                name="password"
                type="password"
                size="md"
                value={password}
                onChange={e => setPassword(e.currentTarget.value)}
              />
              <Text size="xs" mt={2} opacity={0.5}>
                {t('pages.login.passwordRules')}
              </Text>
            </Input.Wrapper>
            <Button
              type="submit"
              mt="md"
              size="md"
              loading={newPasswordMutation.isLoading}
              disabled={password.length < 8}
            >
              {t('_generics.confirm')}
            </Button>
          </>
        )}
      </Stack>
    </form>
  )
}
